import React, { ReactElement, useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { User } from '@/API/models';
import TopUpModal, {
  TopUpModalRef,
} from '@/components/Modals/TopUpModal/TopUpModal';
import { thankYouPageTitle } from '@/components/SubscriptionCard/SubscriptionCard.utils';
import SuccessfulOperationIndicator from '@/components/SVG/SuccessfulOperationIndicator';

const SubscriptionThankYouPage = ({
  subscription_info,
  min_top_up_amount_formatted,
  top_up_deadline_formatted,
}: {
  subscription_info: User['subscription'];
  min_top_up_amount_formatted: string;
  top_up_deadline_formatted: string;
}) => {
  const topUpModalRef = useRef<TopUpModalRef>(null);
  const queryClient = new QueryClient();

  const handleTopUpClick = () => {
    topUpModalRef.current?.openModal();
  };

  useEffect(() => {
    document.title = thankYouPageTitle;
  }, []);

  const renderInfoText: () => ReactElement = () => {
    if (subscription_info?.plan_name === 'Proficient Pioneer') {
      return (
        <>
          Top up your balance now and use it toward your next flight booking!
          Want to get even more out of your subscription? Upgrade for exclusive
          discounts on hourly rates.
        </>
      );
    }

    return (
      <>
        To keep enjoying all your benefits, please top up your balance{' '}
        <span className='font-bold'>
          (minimum {min_top_up_amount_formatted})
        </span>
        . If you don’t top up by{' '}
        <span className='font-bold'>{top_up_deadline_formatted}</span>, your
        plan will automatically be downgraded to Proficient Pioneer, and you’ll
        lose your hourly rate discount.
      </>
    );
  };

  useEffect(() => {
    localStorage.removeItem('client_secret_checkout_subscribe');
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <div className='flex justify-center items-center'>
        <div
          className='flex flex-col items-center gap-10'
          style={{ maxWidth: 678 }}
        >
          <SuccessfulOperationIndicator />

          <h1
            className='text-center font-serif font-bold'
            style={{ fontSize: 30, lineHeight: '35px' }}
          >
            You Have Successfully Subscribed!
          </h1>

          <p
            className='leading-10 text-center text-18px'
            style={{ letterSpacing: '0.15px' }}
          >
            We&apos;re excited to have you on board!
          </p>

          <p
            className='leading-10 text-center text-18px'
            style={{ letterSpacing: '0.15px' }}
          >
            {renderInfoText()}
          </p>

          <div className='flex gap-10 text-center'>
            <a href='/search' className='btn-secondary'>
              Search for Flights
            </a>
            <button
              className='btn'
              style={{ width: 150 }}
              onClick={handleTopUpClick}
            >
              Top Up
            </button>
          </div>
        </div>
      </div>

      <TopUpModal ref={topUpModalRef} />
    </QueryClientProvider>
  );
};

export default SubscriptionThankYouPage;
